import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosInstance } from '../axiosConfig';
import { getColorForAssetType } from '../components/strategies/utils';
import axios from 'axios';
import Cookies from 'js-cookie';

const getInitialCache = () => {
  try {
    const item = window.localStorage.getItem('assetDetailsCache');
    return item ? JSON.parse(item) : {};
  } catch (error) {
    console.warn("Could not parse assetDetailsCache from localStorage", error);
    return {};
  }
};

export const validateToken = createAsyncThunk(
  'strategy/validateToken',
  async (_, { dispatch }) => {
    const cookieData = Cookies.get('PensionCraftUserData');
    let jwtToken = null;
    let parsedCookie = null;
    let userName = null;

    if (cookieData) {
      try {
        parsedCookie = JSON.parse(cookieData);
        jwtToken = parsedCookie.jwt;
        userName = parsedCookie.name;
        if (!jwtToken) {
          console.error('❌ [VALIDATE] No JWT in cookie, redirecting in 5 seconds...');
          setTimeout(() => {
            window.location.href = 'https://pensioncraft.com/investor-education/login/';
          }, 1000);
          throw new Error('JWT token not found in cookie');
        }
      } catch (error) {
        console.error('Error parsing cookie data:', error);
        throw error;
      }
    } else {
      console.error('❌ [VALIDATE] No cookie found, redirecting in 5 seconds...');
      setTimeout(() => {
        window.location.href = 'https://pensioncraft.com/investor-education/login/';
      }, 1000);
      throw new Error('Error reading cookie');
    }

    try {
      const response = await axios.post(
        // todo here we change the endpoints in base of the environment
        'https://pensioncraft.com/wp-json/pensioncraft/v1/validate-token',
        // 'https://staging2.pensioncraft.com/wp-json/pensioncraft/v1/validate-token',
        { token: jwtToken },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );


      if (response.data.isValid) {
        if (response.data.message === 'Token refreshed' && response.data.jwt) {
          const newJwt = response.data.jwt;
          parsedCookie.jwt = newJwt;
          Cookies.set('PensionCraftUserData', JSON.stringify(parsedCookie));
        }
        dispatch(setUserData({ name: userName, isMember: response.data.isMember }));
        return response.data;
      } else {
        console.error('❌ [VALIDATE] Token invalid, redirecting in 5 seconds...');
        // this is redirect!
      }
    } catch (error) {
      console.error('💥 [VALIDATE] API call failed:', error);
      console.error('💥 [VALIDATE] Error details:', error.response?.data);
      // also this it can redirect!
      throw error;
    }
  }
);

export const copyStrategy = createAsyncThunk(
  'strategy/copyStrategy',
  async (strategy, { dispatch }) => {
    try {
      const { id, ...strategyData } = strategy;

      const newStrategyData = {
        ...strategyData,
        name: `${strategyData.name} (Copy)`
      };

      const response = await axiosInstance.post('/strategies/', newStrategyData);
      dispatch(addStrategy(response.data));
      return response.data;
    } catch (error) {
      throw error;
    }
  }
);

export const fetchStrategies = createAsyncThunk(
  'strategy/fetchStrategies',
  async (force = false, { getState }) => {
    const state = getState().strategy;
    const now = Date.now();

    // 🔧 Cache validation: Skip if recently fetched (unless forced)
    if (!force && state.lastFetchTime && (now - state.lastFetchTime) < state.fetchCooldown) {
      return state.strategies; // Return cached data
    }

    const response = await axiosInstance.get('/strategies');
    const assets = state.assets;

    return response.data.map(strategy => ({
      ...strategy,
      assets: strategy.assets.map(asset => {
        // Handle new API format: ticker instead of asset_ticker, weight as string
        const ticker = asset.ticker || asset.asset_ticker;
        const fullAssetInfo = assets.find(a => a.ticker === ticker) || {};
        return {
          ...asset,
          ticker: ticker,
          asset_ticker: ticker,
          weight: parseFloat(asset.weight),
          asset_type: fullAssetInfo.asset_type || 'Unknown',
          asset_description: fullAssetInfo.description,
          color: getColorForAssetType(fullAssetInfo.asset_type),
        };
      }),
    }));
  }
);

export const fetchPublicStrategies = createAsyncThunk(
  'strategy/fetchPublicStrategies',
  async (_, { getState }) => {
    const response = await axiosInstance.get('/strategies/public');
    const assets = getState().strategy.assets;

    return response.data.map(strategy => ({
      ...strategy,
      assets: strategy.assets.map(asset => {
        const ticker = asset.ticker || asset.asset_ticker;
        const fullAssetInfo = assets.find(a => a.ticker === ticker) || {};
        return {
          ...asset,
          ticker: ticker,
          asset_ticker: ticker,
          asset_type: fullAssetInfo.asset_type || 'Unknown',
          asset_description: fullAssetInfo.description,
          color: getColorForAssetType(fullAssetInfo.asset_type),
        };
      }),
      isPublic: true,
    }));
  }
);

export const clonePublicStrategy = createAsyncThunk(
  'strategy/clonePublicStrategy',
  async (strategy) => {
    const { id, ...newStrategy } = strategy;
    const response = await axiosInstance.post('/strategies/', newStrategy);
    return response.data;
  }
);

export const fetchPerformanceData = createAsyncThunk(
  'strategy/fetchPerformanceData',
  async ({
    id,
    start_date,
    end_date,
    currency = 'GBP',
    rebalancing_frequency_in_months = 0,
    investment_amount,
    transaction_fee_amount,
    transaction_fee_type,
  }, { rejectWithValue }) => {
    try {
      const commonParams = {
        start_date,
        end_date,
        currency,
        rebalancing_frequency_in_months,
        investment_amount,
      };

      if (transaction_fee_amount !== undefined) {
        commonParams.transaction_fee_amount = transaction_fee_amount;
      }
      if (transaction_fee_type) {
        commonParams.transaction_fee_type = transaction_fee_type;
      }

      const realParams = { ...commonParams, use_nominal_data: false };
      const nominalParams = { ...commonParams, use_nominal_data: true };

      const realPromise = axiosInstance.get(`/strategies/${id}/performance/`, { params: realParams });
      const nominalPromise = axiosInstance.get(`/strategies/${id}/performance/`, { params: nominalParams });

      const [realResponse, nominalResponse] = await Promise.all([realPromise, nominalPromise]);

      return {
        real: realResponse.data,
        nominal: nominalResponse.data
      };
    } catch (error) {
      console.error('error fetchPerformanceData:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAnnualizedReturnsVolatility = createAsyncThunk(
  'strategy/fetchAnnualizedReturnsVolatility',
  async ({ id, start_date, end_date, currency = 'GBP' }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/strategies/${id}/annualized_returns_volatility/`, {
        params: {
          start_date,
          end_date,
          currency,
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching annualized returns volatility:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data || 'Failed to fetch risk & return data. Please try different dates.');
    }
  }
);

export const fetchCladogram = createAsyncThunk(
  'strategy/fetchCladogram',
  async ({ id, start_date, end_date, currency = 'GBP' }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/strategies/${id}/cladogram/`, {
        params: {
          start_date,
          end_date,
          currency,
          include_default_assets: false,
        },
      });

      const { assets, rows } = response.data.cladogram;

      const nodes = assets.map((asset, index) => ({
        name: asset,
        index: index,
        children: []
      }));

      rows.forEach(({ index1, index2, dissimilarity }) => {
        const node1 = nodes[index1];
        const node2 = nodes[index2];
        const newNode = {
          name: `(${dissimilarity.toFixed(4)})`,
          children: [node1, node2],
        };
        nodes.push(newNode);
      });

      return [nodes[nodes.length - 1]];
    } catch (error) {
      console.error('Error fetching cladogram:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data || 'Failed to fetch cladogram data. Please try different dates.');
    }
  }
);

export const fetchDrawdownData = createAsyncThunk(
  'strategy/fetchDrawdownData',
  async ({
    id,
    start_date,
    end_date,
    currency = 'GBP',
    rebalancing_frequency_in_months = 0,
    investment_amount,
    transaction_fee_amount,
    transaction_fee_type
  }, { rejectWithValue }) => {
    try {
      const params = {
        start_date,
        end_date,
        currency,
        rebalancing_frequency_in_months,
        investment_amount
      };

      if (transaction_fee_amount !== undefined) {
        params.transaction_fee_amount = transaction_fee_amount;
      }
      if (transaction_fee_type) {
        params.transaction_fee_type = transaction_fee_type;
      }


      const response = await axiosInstance.get(`/strategies/${id}/drawdown/`, { params });
      return response.data.drawdown;
    } catch (error) {
      console.warn('Drawdown endpoint not available:', error);
      return rejectWithValue('Drawdown endpoint not available');
    }
  }
);

export const fetchStrategyById = createAsyncThunk(
  'strategy/fetchStrategyById',
  async ({ id, isPublic = false }, { dispatch, getState }) => {
    try {

      // Check if we're coming from famous strategies by looking at URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const fromFamous = urlParams.get('from') === 'famous';

      // If not explicitly specified, check if strategy exists in publicStrategies
      let shouldUsePublicEndpoint = isPublic;
      if (!isPublic && !fromFamous) {
        const state = getState().strategy;
        const existsInPublic = state.publicStrategies.some(s => s.id === parseInt(id));
        shouldUsePublicEndpoint = existsInPublic;
      } else if (fromFamous) {
        shouldUsePublicEndpoint = true;
      }

      const endpoint = shouldUsePublicEndpoint
        ? `/strategies/${id}/public/`
        : `/strategies/${id}/`;

      const response = await axiosInstance.get(endpoint);


      dispatch(updateStrategy(response.data));
      return response.data;
    } catch (error) {
      console.error('Error fetching strategy by ID:', error);
      throw error;
    }
  }
);

const initialState = {
  user: {
    name: null,
    isMember: null,
  },
  isAuthenticated: false,
  strategies: [],
  publicStrategies: [],
  performanceData: [],
  monteCarloData: null,
  assets: [],
  assetsStatus: 'idle',
  cladogramData: null,
  cladogramStatus: 'idle',
  cladogramError: null,
  annualizedReturnsVolatility: null,
  annualizedReturnsVolatilityStatus: 'idle',
  annualizedReturnsVolatilityError: null,
  status: 'idle',
  publicStatus: 'idle',
  strategyByIdStatus: 'idle',
  performanceStatus: 'idle',
  monteCarloStatus: 'idle',
  drawdownData: [],
  drawdownStatus: 'idle',
  error: null,
  dataLoaded: false,
  lastLoadedParams: null,
  publicError: null,
  performanceError: null,
  monteCarloError: null,
  drawdownError: null,
  nominalPerformanceData: null,
  nominalPerformanceStatus: 'idle',
  nominalPerformanceError: null,
  transactionFee: 0,
  transactionFeeType: '£',
  settings: {
    investmentAmount: "1000",
    rebalancingPeriod: 0,
    transactionFeeValue: "",
    transactionFeeType: "-",
    currency: "GBP",
  },
  calculated: false,
  assetDetailsCache: getInitialCache(),
  lastFetchTime: null,
  fetchCooldown: 30000, // 30 seconds
};
const strategySlice = createSlice({
  name: 'strategy',
  initialState,
  reducers: {
    setUserData: (state, action) => {
      state.user.name = action.payload.name;
      state.user.isMember = action.payload.isMember;
      state.isAuthenticated = true;
    },
    setCalculated: (state, action) => {
      state.calculated = action.payload;
    },
    cacheAssetDetails: (state, action) => {
      const asset = action.payload;
      if (asset && asset.ticker) {
        state.assetDetailsCache[asset.ticker] = asset;
        try {
          //  update localStorage
          window.localStorage.setItem('assetDetailsCache', JSON.stringify(state.assetDetailsCache));
        } catch (error) {
          console.warn("Could not save assetDetailsCache to localStorage", error);
        }
      }
    },
    addStrategy: (state, action) => {
      const strategyWithColors = {
        ...action.payload,
        assets: action.payload.assets.map(asset => ({
          ...asset,
          color: getColorForAssetType(asset.asset_type),
        })),
      };
      state.strategies.push(strategyWithColors);
      state.performanceData = null;
    },
    removeStrategy: (state, action) => {
      state.strategies = state.strategies.filter((s) => s.id !== action.payload);
    },
    setAssets: (state, action) => {
      state.assets = action.payload;
    },
    setTransactionFee: (state, action) => {
      state.transactionFee = action.payload.fee;
      state.transactionFeeType = action.payload.feeType;
    },
    setSettings: (state, action) => {
      state.settings = { ...state.settings, ...action.payload };
    },
    resetStrategyData: (state) => {
      state.performanceData = [];
      state.drawdownData = [];
    },
    resetSettings: (state) => {
      state.settings = initialState.settings;
      state.performanceData = null;
    },
                updateStrategy: (state, action) => {

      const index = state.strategies.findIndex(s => s.id === action.payload.id);

      // Process the assets to maintain the enhanced format (colors, types, etc.)
      const processedStrategy = {
        ...action.payload,
        assets: action.payload.assets.map(asset => {
          const ticker = asset.ticker || asset.asset_ticker;
          const fullAssetInfo = state.assets.find(a => a.ticker === ticker) || {};
          return {
            ...asset,
            ticker: ticker,
            asset_ticker: ticker,
            weight: typeof asset.weight === 'string' ? parseFloat(asset.weight) : asset.weight,
            asset_type: fullAssetInfo.asset_type || 'Unknown',
            asset_description: fullAssetInfo.description,
            color: getColorForAssetType(fullAssetInfo.asset_type),
          };
        }),
      };


      if (index !== -1) {
        // Update existing strategy
        state.strategies[index] = processedStrategy;
      } else {
        state.strategies.push(processedStrategy);
      }

      // Check if it's also a public strategy that needs updating
      const isPublicStrategy = state.publicStrategies.some(s => s.id === action.payload.id);
      if (isPublicStrategy) {
        // If it's a public strategy, update it in the publicStrategies array
        const publicIndex = state.publicStrategies.findIndex(s => s.id === action.payload.id);
        if (publicIndex !== -1) {
          const processedPublicStrategy = {
            ...action.payload,
            isPublic: true,
            assets: action.payload.assets.map(asset => {
              const ticker = asset.ticker || asset.asset_ticker;
              const fullAssetInfo = state.assets.find(a => a.ticker === ticker) || {};
              return {
                ...asset,
                ticker: ticker,
                asset_ticker: ticker,
                weight: typeof asset.weight === 'string' ? parseFloat(asset.weight) : asset.weight,
                asset_type: fullAssetInfo.asset_type || 'Unknown',
                asset_description: fullAssetInfo.description,
                color: getColorForAssetType(fullAssetInfo.asset_type),
              };
            }),
          };
          state.publicStrategies[publicIndex] = processedPublicStrategy;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
    .addCase(validateToken.pending, (state) => {
      state.authStatus = 'loading';
    })
    .addCase(validateToken.fulfilled, (state, action) => {
      state.authStatus = 'succeeded';
      state.user.name = action.payload.name;
      state.user.isMember = action.payload.isMember;
      state.isAuthenticated = true;
    })
    .addCase(validateToken.rejected, (state, action) => {
      state.authStatus = 'failed';
      state.isAuthenticated = false;
      state.error = action.error.message;
    })
      .addCase(fetchStrategies.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchStrategies.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // 🔧 Track when strategies were last fetched
        state.lastFetchTime = Date.now();

        const newStrategies = action.payload.map(newStrategy => {
          const existingStrategy = state.strategies.find(s => s.id === newStrategy.id);

          if (existingStrategy && existingStrategy.start_dates && existingStrategy.end_dates) {
            return existingStrategy;
          }
          return newStrategy;
        });


        const existingIds = newStrategies.map(s => s.id);
        const preservedStrategies = state.strategies.filter(s => !existingIds.includes(s.id));

        state.strategies = [...newStrategies, ...preservedStrategies];
      })
      .addCase(fetchStrategies.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchPublicStrategies.pending, (state) => {
        state.publicStatus = 'loading';
      })
      .addCase(clonePublicStrategy.fulfilled, (state, action) => {
        state.strategies.push(action.payload);
      })
      .addCase(fetchPublicStrategies.fulfilled, (state, action) => {
        state.publicStatus = 'succeeded';
        state.publicStrategies = action.payload;
      })
      .addCase(fetchPublicStrategies.rejected, (state, action) => {
        state.publicStatus = 'failed';
        state.publicError = action.error.message;
      })
      .addCase(fetchPerformanceData.pending, (state) => {
        state.performanceStatus = 'loading';
      })
      .addCase(fetchPerformanceData.fulfilled, (state, action) => {
        state.performanceStatus = 'succeeded';
        state.performanceData = action.payload.real;
        state.nominalPerformanceData = action.payload.nominal;
        state.dataLoaded = true;
        state.lastLoadedParams = action.meta.arg;
      })
      .addCase(fetchPerformanceData.rejected, (state, action) => {
        state.performanceStatus = 'failed';
        state.performanceError = action.error.message;
      })
      .addCase(fetchAnnualizedReturnsVolatility.pending, (state) => {
        state.annualizedReturnsVolatilityStatus = 'loading';
      })
      .addCase(fetchAnnualizedReturnsVolatility.fulfilled, (state, action) => {
        state.annualizedReturnsVolatilityStatus = 'succeeded';
        state.annualizedReturnsVolatility = action.payload;
      })
      .addCase(fetchAnnualizedReturnsVolatility.rejected, (state, action) => {
        state.annualizedReturnsVolatilityStatus = 'failed';
        state.annualizedReturnsVolatilityError = action.error.message;
      })
      .addCase(fetchCladogram.pending, (state) => {
        state.cladogramStatus = 'loading';
      })
      .addCase(fetchCladogram.fulfilled, (state, action) => {
        state.cladogramStatus = 'succeeded';
        state.cladogramData = action.payload;
      })
      .addCase(fetchCladogram.rejected, (state, action) => {
        state.cladogramStatus = 'failed';
        state.cladogramError = action.error.message;
      })
      .addCase(fetchDrawdownData.pending, (state) => {
        state.drawdownStatus = 'loading';
      })
      .addCase(fetchDrawdownData.fulfilled, (state, action) => {
        state.drawdownStatus = 'succeeded';
        state.drawdownData = action.payload;
      })
      .addCase(fetchDrawdownData.rejected, (state, action) => {
        state.drawdownStatus = 'failed';
        state.drawdownError = action.error.message;
      })
      .addCase(fetchStrategyById.pending, (state) => {
        state.strategyByIdStatus = 'loading';
      })
      .addCase(fetchStrategyById.fulfilled, (state, action) => {
        state.strategyByIdStatus = 'succeeded';
      })
      .addCase(fetchStrategyById.rejected, (state, action) => {
        state.strategyByIdStatus = 'failed';
      });
  },
});

export const { addStrategy, removeStrategy, setAssets, setTransactionFee, setCalculated, setSettings, resetStrategyData , resetSettings, setUserData, updateStrategy, cacheAssetDetails } = strategySlice.actions;

export default strategySlice.reducer;
